import {
  DEFAULT_SALES_PLAN,
  type ContangoCondition,
  type ListingWrite,
  type MandateListingKind,
  type MandateListingWrite,
  type MandateResponse,
  type MandateWriteRequest,
  type ProductionRange,
  type SalesPlan,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";

export type ListedKind = Exclude<MandateListingKind, "pay_as_produced">;

const PERIOD_WIDTHS: readonly PeriodWidth[] = [
  "month",
  "quarter",
  "half_year",
  "year",
];

/** A recurring template at one width: a volume per slot of the year. */
export interface RecurringDraft {
  volumes: Record<string, number>;
}

/**
 * The editor's own state. Every listing mode — pay-as-produced, and each listed kind at each width —
 * keeps its own entries, so switching modes and back finds them as they were left.
 *
 * The wire's `MandateWriteRequest` and `MandateResponse` carry only the listing policy the mandate actually
 * is: `requestFromForm` sends the selected mode alone, and `formFromMandate` fills that one mode and
 * leaves every other blank.
 *
 * @property last_listed_kind - The kind a pick of width returns to from pay-as-produced; equals
 *   `listing_kind` whenever that is a listed kind
 * @property entry_widths - The width each listed kind is on, so switching kind returns to its own
 * @property sales_plan - Sent with a listed kind's listing and kept through pay-as-produced,
 *   which lists none, so switching back restores the choice; a pay-as-produced mandate loads with the
 *   default
 * @property production_limited - The listing's "restrict to a fixed production period" box. One range
 *   for the mandate, kept through every mode: recurring and pay-as-produced send it, one-off sends
 *   none. While the box is off both ends go as null, so a save drops the months.
 * @property production_from - The wire's `ProductionRange.production_from`; null for an open start
 * @property production_until - The wire's `ProductionRange.production_until`; null for an open end
 */
export interface MandateEditorForm {
  version: number | null;
  name: string;
  description: string | null;
  listing_kind: MandateListingKind;
  last_listed_kind: ListedKind;
  entry_widths: Record<ListedKind, PeriodWidth>;
  device_scope: MandateWriteRequest["device_scope"];
  member_company_ids: number[];
  allowlist_device_ids: number[];
  minimum_price: string | null;
  contango_condition: ContangoCondition;
  archive_on_delivery: boolean;
  advanced_listing: boolean;
  production_limited: boolean;
  production_from: string | null;
  production_until: string | null;
  sales_plan: SalesPlan;
  recurring: Record<PeriodWidth, RecurringDraft>;
  one_off: Record<PeriodWidth, ListingWrite[]>;
}

/**
 * A mandate the editor has not saved yet: quarterly recurring in the simple listing editor, every
 * member device, no floor, sold four times a year from next year's production.
 */
export function blankMandateForm(): MandateEditorForm {
  return {
    version: null,
    name: "",
    description: null,
    listing_kind: "recurring",
    last_listed_kind: "recurring",
    entry_widths: { one_off: "quarter", recurring: "quarter" },
    device_scope: "all_devices",
    member_company_ids: [],
    allowlist_device_ids: [],
    minimum_price: null,
    contango_condition: "none",
    archive_on_delivery: false,
    advanced_listing: false,
    production_limited: false,
    production_from: null,
    production_until: null,
    sales_plan: { ...DEFAULT_SALES_PLAN },
    recurring: byWidth(blankRecurringDraft),
    one_off: byWidth(() => []),
  };
}

/** The width a listed kind is on; for pay-as-produced, the one a pick of width returns to. */
export function selectedWidth(form: MandateEditorForm): PeriodWidth {
  return form.entry_widths[form.last_listed_kind];
}

/**
 * The saved mandate as the editor form sees it — the inverse of a save. A mandate whose listing the
 * viewer may not see (the escrow-remaining view, which renders no form) reads as a blank listing.
 */
export function formFromMandate(mandate: MandateResponse): MandateEditorForm {
  const blank = blankMandateForm();
  return {
    ...blank,
    version: mandate.version,
    name: mandate.name,
    description: mandate.description,
    device_scope: mandate.device_scope,
    member_company_ids: [...mandate.member_company_ids],
    allowlist_device_ids: [...mandate.allowlist_device_ids],
    minimum_price: mandate.minimum_price,
    contango_condition: mandate.contango_condition,
    archive_on_delivery: mandate.archive_on_delivery,
    advanced_listing: mandate.advanced_listing,
    ...listingFormOf(mandate.listing),
  };
}

type ListingFields = Pick<
  MandateEditorForm,
  | "listing_kind"
  | "last_listed_kind"
  | "entry_widths"
  | "recurring"
  | "one_off"
  | "sales_plan"
  | "production_limited"
  | "production_from"
  | "production_until"
>;

function listingFormOf(
  listing: MandateListingWrite | null
): Partial<ListingFields> {
  if (listing === null) return {};
  const blank = blankMandateForm();
  // A pay-as-produced mandate picking a width becomes one-off, as it always has.
  if (listing.kind === "pay_as_produced")
    return {
      listing_kind: "pay_as_produced",
      last_listed_kind: "one_off",
      ...rangeFormOf(listing),
    };
  const entry_widths = {
    ...blank.entry_widths,
    [listing.kind]: listing.entry_width,
  };
  if (listing.kind === "recurring") {
    return {
      listing_kind: "recurring",
      last_listed_kind: "recurring",
      entry_widths,
      sales_plan: { ...listing.sales_plan },
      recurring: {
        ...blank.recurring,
        [listing.entry_width]: { volumes: { ...listing.volumes } },
      },
      ...rangeFormOf(listing),
    };
  }
  return {
    listing_kind: "one_off",
    last_listed_kind: "one_off",
    entry_widths,
    sales_plan: { ...listing.sales_plan },
    one_off: {
      ...blank.one_off,
      [listing.entry_width]: listing.listings.map((row) => ({ ...row })),
    },
  };
}

/** The form as the write routes take it: the common fields plus the one listing policy it is in. */
export function requestFromForm(form: MandateEditorForm): MandateWriteRequest {
  return {
    version: form.version,
    name: form.name,
    description: form.description,
    device_scope: form.device_scope,
    member_company_ids: [...form.member_company_ids],
    allowlist_device_ids: [...form.allowlist_device_ids],
    minimum_price: form.minimum_price,
    contango_condition: form.contango_condition,
    archive_on_delivery: form.archive_on_delivery,
    advanced_listing: form.advanced_listing,
    listing: listingFromForm(form),
  };
}

/**
 * The listing policy the form is in, as the write routes take it. A listed kind always carries the
 * strategy — the routes refuse one without it — and pay-as-produced, which lists nothing, none.
 * Recurring and pay-as-produced carry the production range; a one-off's rows name their periods.
 */
export function listingFromForm(form: MandateEditorForm): MandateListingWrite {
  if (form.listing_kind === "pay_as_produced")
    return { kind: "pay_as_produced", ...productionRangeOf(form) };
  const width = selectedWidth(form);
  if (form.listing_kind === "recurring") {
    const draft = form.recurring[width];
    return {
      kind: "recurring",
      entry_width: width,
      volumes: { ...draft.volumes },
      sales_plan: { ...form.sales_plan },
      ...productionRangeOf(form),
    };
  }
  return {
    kind: "one_off",
    entry_width: width,
    sales_plan: { ...form.sales_plan },
    listings: form.one_off[width].map((listing) => ({
      period_start: listing.period_start,
      period_end: listing.period_end,
      volume_mwh: listing.volume_mwh,
    })),
  };
}

/** A deep copy; `structuredClone` refuses the reactive proxies the editor holds the form in. */
export function cloneForm(source: MandateEditorForm): MandateEditorForm {
  return {
    ...source,
    entry_widths: { ...source.entry_widths },
    member_company_ids: [...source.member_company_ids],
    allowlist_device_ids: [...source.allowlist_device_ids],
    recurring: byWidth((width) => ({
      volumes: { ...source.recurring[width].volumes },
    })),
    one_off: byWidth((width) =>
      source.one_off[width].map((listing) => ({ ...listing }))
    ),
  };
}

/** The range the form would send; an unticked box sends both ends open. */
export function productionRangeOf(form: MandateEditorForm): ProductionRange {
  return form.production_limited
    ? {
        production_from: form.production_from,
        production_until: form.production_until,
      }
    : { production_from: null, production_until: null };
}

function rangeFormOf(
  range: ProductionRange
): Pick<
  MandateEditorForm,
  "production_limited" | "production_from" | "production_until"
> {
  return {
    production_limited:
      range.production_from !== null || range.production_until !== null,
    production_from: range.production_from,
    production_until: range.production_until,
  };
}

function blankRecurringDraft(): RecurringDraft {
  return { volumes: {} };
}

function byWidth<T>(
  valueOf: (width: PeriodWidth) => T
): Record<PeriodWidth, T> {
  return Object.fromEntries(
    PERIOD_WIDTHS.map((width) => [width, valueOf(width)])
  ) as Record<PeriodWidth, T>;
}
